import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Layers,
  TrendingDown,
  Archive,
  AlertTriangle,
  Search,
  Trophy,
  Activity,
  Sparkles,
  ArrowLeft,
  ChevronDown,
} from "lucide-react";
import { getCherries, track } from "../lib/api";

/**
 * CherryPicker — the universal "wow moment" component.
 * Replaces hand-tuned per-client cards with a generic engine output.
 */

const ICONS = {
  Users,
  Layers,
  TrendingDown,
  Archive,
  AlertTriangle,
  Search,
  Trophy,
  Activity,
};

const COLOR_STYLES = {
  red: {
    text: "text-red-300",
    bg: "bg-red-500/8",
    border: "border-red-500/25",
    glow: "hover:shadow-[0_0_30px_-10px_rgba(239,68,68,0.4)]",
    chip: "bg-red-500/15 text-red-300",
    label: "סיכון גבוה",
  },
  amber: {
    text: "text-amber-300",
    bg: "bg-amber-500/8",
    border: "border-amber-500/25",
    glow: "hover:shadow-[0_0_30px_-10px_rgba(245,158,11,0.4)]",
    chip: "bg-amber-500/15 text-amber-300",
    label: "לבדיקה",
  },
  emerald: {
    text: "text-emerald-300",
    bg: "bg-emerald-500/8",
    border: "border-emerald-500/25",
    glow: "hover:shadow-[0_0_30px_-10px_rgba(16,185,129,0.4)]",
    chip: "bg-emerald-500/15 text-emerald-300",
    label: "הזדמנות",
  },
  cyan: {
    text: "text-cyan-300",
    bg: "bg-cyan-500/8",
    border: "border-cyan-500/25",
    glow: "hover:shadow-[0_0_30px_-10px_rgba(0,229,255,0.4)]",
    chip: "bg-cyan-500/15 text-cyan-300",
    label: "תנודה",
  },
};

function fmtMoney(n) {
  if (n == null) return "—";
  const v = Math.abs(n);
  if (v >= 1_000_000) return `₪${(n / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `₪${Math.round(n / 1_000)}K`;
  return `₪${Math.round(n)}`;
}

function formatRelative(isoTs) {
  try {
    const then = new Date(isoTs);
    const diffSec = Math.floor((Date.now() - then.getTime()) / 1000);
    if (diffSec < 60) return "לפני פחות מדקה";
    if (diffSec < 3600) return `לפני ${Math.floor(diffSec / 60)} דקות`;
    // Fallback to HH:MM
    return then.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export default function CherryPicker({ onAskAi }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    getCherries(10)
      .then((r) => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="mb-6" data-testid="cherry-picker-loading">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2.5">
          🍒 הדובדבנים שמצאנו בעסק שלך
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="bg-card border border-border rounded-md p-4 h-32 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const cherries = data?.cherries || [];
  const visibleCherries = showAll ? cherries : cherries.slice(0, 3);
  const sources = data?.data_sources || [];

  if (cherries.length === 0) {
    return (
      <div className="mb-6 bg-card border border-border rounded-md p-6 text-center">
        <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />
        <div className="text-sm text-zinc-400">Pulse סורק את הנתונים שלך...</div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="mb-6"
      data-testid="cherry-picker"
    >
      {/* Eyebrow + credibility line */}
      <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            🍒 3 הדברים שאתה חייב לדעת
          </div>
          {sources.length > 0 && (
            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-1.5 flex-wrap">
              <span>Pulse ניתח</span>
              {sources.map((s, i) => (
                <span key={s.key} className="inline-flex items-center gap-1">
                  <span className="text-primary">✓</span>
                  <span className="text-zinc-400">{s.label}</span>
                  <span className="text-zinc-600 font-mono">({s.rows.toLocaleString()})</span>
                  {i < sources.length - 1 && <span className="text-zinc-700">·</span>}
                </span>
              ))}
            </div>
          )}
          {data?.generated_at && (
            <div className="text-[10px] text-zinc-600 mt-1 flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500/70 animate-pulse" />
              <span>נותח לאחרונה: {formatRelative(data.generated_at)}</span>
            </div>
          )}
        </div>
        <div className="text-[10px] text-zinc-500 font-mono">
          {showAll ? `${cherries.length} ממצאים פעילים` : `${Math.min(3, cherries.length)} מתוך ${cherries.length}`}
        </div>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-2 ${showAll ? "lg:grid-cols-3 xl:grid-cols-4" : "lg:grid-cols-3"} gap-3`}>
        {visibleCherries.map((c, i) => (
          <CherryCard
            key={c.id}
            cherry={c}
            index={i}
            expanded={expandedId === c.id}
            onToggle={() => setExpandedId(expandedId === c.id ? null : c.id)}
            onDrill={() => {
              track("cherry_drill", "/", { id: c.id });
              if (c.drill_to) navigate(c.drill_to);
            }}
            onAskAi={() => {
              track("cherry_ask_ai", "/", { id: c.id });
              if (onAskAi) onAskAi(c.headline);
            }}
          />
        ))}
      </div>

      {cherries.length > 3 && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => {
              track("cherry_show_all", "/", { newState: !showAll });
              setShowAll((s) => !s);
            }}
            className="text-xs text-zinc-400 hover:text-primary border border-border hover:border-primary/40 rounded-md px-4 py-1.5 transition-all flex items-center gap-1.5"
            data-testid="cherry-show-all"
          >
            {showAll
              ? "הסתר חלק מהממצאים"
              : `ראה עוד ${cherries.length - 3} ממצאים`}
            <ChevronDown className={`w-3 h-3 transition-transform ${showAll ? "rotate-180" : ""}`} />
          </button>
        </div>
      )}
    </motion.div>
  );
}

function CherryCard({ cherry, index, expanded, onToggle, onDrill, onAskAi }) {
  const Icon = ICONS[cherry.icon] || Sparkles;
  const style = COLOR_STYLES[cherry.color] || COLOR_STYLES.cyan;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 + index * 0.05 }}
      className={`relative bg-card border ${style.border} rounded-md p-4 text-right transition-all overflow-hidden ${style.glow}`}
      data-testid={`cherry-${cherry.id}`}
    >
      {/* Subtle background gradient */}
      <div className={`absolute inset-0 ${style.bg} opacity-50 pointer-events-none`} />

      <div className="relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono ${style.chip}`}>
            {style.label}
          </div>
          <Icon className={`w-4 h-4 ${style.text}`} strokeWidth={1.5} />
        </div>

        {/* Headline */}
        <div className={`text-base font-semibold ${style.text} leading-snug mb-1`}>
          {cherry.headline}
        </div>

        {/* Subtext */}
        <div className="text-[11px] text-zinc-400 leading-relaxed mb-3">
          {cherry.subtext}
        </div>

        {/* Metrics strip */}
        <div className="grid grid-cols-3 gap-1 py-2 border-y border-border/40 mb-3">
          {(cherry.metrics || []).slice(0, 3).map((m, i) => (
            <div key={i} className="text-center min-w-0">
              <div className={`text-xs font-mono font-semibold ${style.text} leading-none truncate`}>
                {m.value}
              </div>
              <div className="text-[9px] text-zinc-500 uppercase tracking-wider mt-1 truncate">
                {m.label}
              </div>
            </div>
          ))}
        </div>

        {/* Expand/details */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="space-y-1.5 py-2">
                {(cherry.details || []).slice(0, 5).map((d, i) => (
                  <div key={i} className="text-[11px] text-zinc-400 flex items-baseline gap-2 leading-relaxed">
                    <span className={`${style.text} font-mono shrink-0 text-[10px]`}>
                      {i + 1}.
                    </span>
                    <span className="flex-1 truncate text-foreground">{d.name}</span>
                    <span className={`${style.text} font-mono shrink-0 text-[10px]`}>
                      {d.value != null ? fmtMoney(d.value) : (d.pct != null ? `${d.pct}%` : "")}
                    </span>
                  </div>
                ))}
              </div>
              <div className="text-[11px] text-zinc-300 bg-[#0A0A0E]/60 rounded p-2 mt-2 leading-relaxed border border-border/40">
                <span className={`${style.text} font-medium`}>פעולה מומלצת: </span>
                {cherry.action}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Footer actions */}
        <div className="flex items-center gap-2 mt-2">
          <button
            type="button"
            onClick={onToggle}
            className="flex-1 text-[11px] text-zinc-400 hover:text-foreground py-1.5 rounded transition-colors flex items-center justify-center gap-1"
            data-testid={`cherry-${cherry.id}-toggle`}
          >
            {expanded ? "כווץ" : "פרט"}
            <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
          {cherry.drill_to && (
            <button
              type="button"
              onClick={onDrill}
              className="text-[11px] text-primary hover:text-primary/80 py-1.5 px-2 rounded transition-colors flex items-center gap-1"
              data-testid={`cherry-${cherry.id}-drill`}
            >
              לדף <ArrowLeft className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
